import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

const ViewProductModal = ({ isOpen, onClose, productData }) => {
    if (!isOpen || !productData) return null;

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return moment(dateString).format('DD-MM-YYYY');
        } catch (error) {
            return dateString;
        }
    };

    const formatDimensions = (dimensions) => {
        if (!dimensions) return 'N/A';
        
        if (typeof dimensions === 'string') {
            try {
                dimensions = JSON.parse(dimensions);
            } catch (error) {
                return dimensions;
            }
        }
        
        if (dimensions.length && dimensions.width && dimensions.height) {
            return `${dimensions.length} x ${dimensions.width} x ${dimensions.height} cm`;
        }
        return 'N/A';
    };

    return (
        <div 
            className={`modal-overlay ${isOpen ? 'show' : ''}`} 
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1040,
                display: isOpen ? 'flex' : 'none',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px'
            }}
            onClick={onClose}
        >
            <div 
                className="modal-content"
                style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    width: '100%',
                    maxWidth: '800px',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-header" style={{
                    padding: '1rem 1.5rem',
                    borderBottom: '1px solid #dee2e6',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: '#f8f9fa'
                }}>
                    <h5 className="modal-title" style={{ margin: 0, fontWeight: '600' }}>
                        Product Details
                    </h5>
                    <button 
                        type="button" 
                        className="btn-close" 
                        onClick={onClose}
                        style={{
                            border: 'none',
                            background: 'none',
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                            color: '#6c757d',
                            padding: '0',
                            width: '30px',
                            height: '30px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        &times;
                    </button>
                </div>
                
                <div className="modal-body" style={{ padding: '1.5rem', flex: 1 }}>
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <h6 style={{ color: '#495057', borderBottom: '1px solid #dee2e6', paddingBottom: '0.5rem', marginBottom: '1rem' }}>
                                Product Information
                            </h6>
                            <table className="table table-sm table-borderless">
                                <tbody>
                                    <tr>
                                        <th width="40%" style={{ padding: '0.5rem 0', color: '#6c757d' }}>Product Name:</th>
                                        <td style={{ padding: '0.5rem 0', fontWeight: '500' }}>{productData.product_name || productData.Product || 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <th style={{ padding: '0.5rem 0', color: '#6c757d' }}>SKU:</th>
                                        <td style={{ padding: '0.5rem 0' }}>{productData.sku || 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <th style={{ padding: '0.5rem 0', color: '#6c757d' }}>HSN Code:</th>
                                        <td style={{ padding: '0.5rem 0' }}>{productData.hsn_code || 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <th style={{ padding: '0.5rem 0', color: '#6c757d' }}>Item Code:</th>
                                        <td style={{ padding: '0.5rem 0' }}>{productData.item_code || 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <th style={{ padding: '0.5rem 0', color: '#6c757d' }}>Category:</th>
                                        <td style={{ padding: '0.5rem 0' }}>{productData.category_name || productData.category || 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <th style={{ padding: '0.5rem 0', color: '#6c757d' }}>Brand:</th>
                                        <td style={{ padding: '0.5rem 0' }}>{productData.brand_name || productData.brand || 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <th style={{ padding: '0.5rem 0', color: '#6c757d' }}>Unit:</th>
                                        <td style={{ padding: '0.5rem 0' }}>{productData.unit_name || productData.unit || 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <th style={{ padding: '0.5rem 0', color: '#6c757d' }}>Status:</th>
                                        <td style={{ padding: '0.5rem 0' }}>
                                            <span className={`badge ${productData.status === 'Active' || productData.Status === 'Active' ? 'badge-success' : 'badge-danger'}`}>
                                                {productData.status || productData.Status || 'N/A'}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        
                        <div className="col-md-6 mb-3">
                            <h6 style={{ color: '#495057', borderBottom: '1px solid #dee2e6', paddingBottom: '0.5rem', marginBottom: '1rem' }}>
                                Product Details
                            </h6>
                            <table className="table table-sm table-borderless">
                                <tbody>
                                    <tr>
                                        <th width="40%" style={{ padding: '0.5rem 0', color: '#6c757d' }}>Weight:</th>
                                        <td style={{ padding: '0.5rem 0' }}>{productData.weight ? `${productData.weight} kg` : 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <th style={{ padding: '0.5rem 0', color: '#6c757d' }}>Dimensions:</th>
                                        <td style={{ padding: '0.5rem 0' }}>{formatDimensions(productData.dimensions)}</td>
                                    </tr>
                                    <tr>
                                        <th style={{ padding: '0.5rem 0', color: '#6c757d' }}>Price:</th>
                                        <td style={{ padding: '0.5rem 0' }}>{productData.price ? `$${parseFloat(productData.price).toFixed(2)}` : 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <th style={{ padding: '0.5rem 0', color: '#6c757d' }}>Quantity:</th>
                                        <td style={{ padding: '0.5rem 0' }}>{productData.quantity || '0'}</td>
                                    </tr>
                                    {productData.has_warranty && (
                                        <>
                                            <tr>
                                                <th style={{ padding: '0.5rem 0', color: '#6c757d' }}>Warranty Period:</th>
                                                <td style={{ padding: '0.5rem 0' }}>{productData.warranty_period || 'N/A'}</td>
                                            </tr>
                                            <tr>
                                                <th style={{ padding: '0.5rem 0', color: '#6c757d' }}>Warranty Type:</th>
                                                <td style={{ padding: '0.5rem 0' }}>{productData.warranty_type || 'N/A'}</td>
                                            </tr>
                                        </>
                                    )}
                                    {productData.has_expiry && (
                                        <tr>
                                            <th style={{ padding: '0.5rem 0', color: '#6c757d' }}>Expiry Date:</th>
                                            <td style={{ padding: '0.5rem 0' }}>{formatDate(productData.expiry_date)}</td>
                                        </tr>
                                    )}
                                    {productData.has_manufacturer && (
                                        <tr>
                                            <th style={{ padding: '0.5rem 0', color: '#6c757d' }}>Manufactured Date:</th>
                                            <td style={{ padding: '0.5rem 0' }}>{formatDate(productData.manufactured_date)}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    
                    <div className="row">
                        <div className="col-12 mb-3">
                            <h6 style={{ color: '#495057', borderBottom: '1px solid #dee2e6', paddingBottom: '0.5rem', marginBottom: '1rem' }}>
                                Description
                            </h6>
                            <div className="border rounded p-3" style={{ 
                                minHeight: '100px', 
                                backgroundColor: '#f8f9fa',
                                fontSize: '0.95rem',
                                lineHeight: '1.6'
                            }}>
                                {productData.description || 'No description available'}
                            </div>
                        </div>
                    </div>
                    
                    {productData.images && productData.images.length > 0 && (
                        <div className="row">
                            <div className="col-12 mb-3">
                                <h6 style={{ color: '#495057', borderBottom: '1px solid #dee2e6', paddingBottom: '0.5rem', marginBottom: '1rem' }}>
                                    Images
                                </h6>
                                <div className="d-flex flex-wrap gap-3">
                                    {productData.images.map((img, index) => (
                                        <div key={index} className="border rounded p-2" style={{ 
                                            width: '120px', 
                                            height: '120px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <img 
                                                src={img.path || img} 
                                                alt={`Product ${index + 1}`}
                                                style={{ 
                                                    maxWidth: '100%', 
                                                    maxHeight: '100%', 
                                                    objectFit: 'contain' 
                                                }}
                                                onError={(e) => {
                                                    e.target.src = 'https://via.placeholder.com/100x100?text=Image';
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                    
                    <div className="row">
                        <div className="col-12">
                            <h6 style={{ color: '#495057', borderBottom: '1px solid #dee2e6', paddingBottom: '0.5rem', marginBottom: '1rem' }}>
                                Additional Information
                            </h6>
                            <table className="table table-sm table-borderless">
                                <tbody>
                                    <tr>
                                        <th width="40%" style={{ padding: '0.5rem 0', color: '#6c757d' }}>Created At:</th>
                                        <td style={{ padding: '0.5rem 0' }}>{formatDate(productData.created_at)}</td>
                                    </tr>
                                    <tr>
                                        <th style={{ padding: '0.5rem 0', color: '#6c757d' }}>Updated At:</th>
                                        <td style={{ padding: '0.5rem 0' }}>{formatDate(productData.updated_at)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                
                <div className="modal-footer" style={{
                    padding: '1rem 1.5rem',
                    borderTop: '1px solid #dee2e6',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    backgroundColor: '#f8f9fa'
                }}>
                    <button 
                        type="button" 
                        className="btn btn-secondary" 
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

ViewProductModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    productData: PropTypes.object
};

export default ViewProductModal;